import { PlansConfiguration } from '../types/planUser'
import { AIFeatureConfig } from '../types/planUser'

// Default values for shipment limits
const TRIAL_PREMIUM_LIMIT = import.meta.env.VITE_TRIAL_PREMIUM_SHIPMENTS_LIMIT ? 
  parseInt(import.meta.env.VITE_TRIAL_PREMIUM_SHIPMENTS_LIMIT) : 5;

const FREEMIUM_LIMIT = import.meta.env.VITE_FREEMIUM_SHIPMENTS_LIMIT ? 
  parseInt(import.meta.env.VITE_FREEMIUM_SHIPMENTS_LIMIT) : 3;

// Definición de módulos por plan
export const planModules: PlansConfiguration = {
  'Trial Premium': {
    modules: ['tracking', 'shipments', 'emails', 'settings', 'ai-agents', 'quotes', 'purchase-orders', 'import_data', 'fields-management', 'documents', 'alerts', 'usage-analytics', 'support', 'warehouse', 'docgen', 'dashboards', 'assistant', 'reports', 'control-tower', 'order-audit', 'container-control'],
    features: ['basic_tracking', 'basic_shipments', 'basic_settings'],
    routes: ['/trial/start', '/trial/company', '/trial/industry', '/trial/volume', '/trial/references', '/trial/problems', '/trial/source', '/trial/complete','/login', '/signup','/dashboard/tracking-request', '/dashboard/shipments', '/dashboard/emails', '/dashboard/tracking-results', '/dashboard/billing', '/dashboard/settings', '/dashboard/settings/data-import', '/dashboard/settings/fields', '/dashboard/not-found', '/dashboard/purchase-orders', '/dashboard/alerts', '/dashboard/support', '/dashboard/support/new', '/dashboard/support/tickets', '/dashboard/support/tickets/new', '/dashboard/support/help', '/dashboard/support/help/questions', '/dashboard/support/help/tours'],

    ai: {
      assistant: {
        enabled: true,
        availableActions: ['analyze-shipment', 'extract-document-data', 'compare-documents', 'validate-invoice', 'reconcile-purchase-orders']
      }
    },
    shipments: {
      maxItems: TRIAL_PREMIUM_LIMIT
    }
  },
  'Freemium': {
    modules: ['tracking', 'shipments', 'emails', 'settings', 'quotes', 'purchase-orders', 'import_data', 'fields-management', 'documents', 'alerts', 'usage-analytics', 'support', 'warehouse', 'docgen', 'dashboards', 'assistant', 'reports', 'control-tower', 'order-audit', 'container-control'],
    features: ['basic_tracking', 'basic_shipments', 'basic_settings'],
    routes: ['/trial/start', '/trial/company', '/trial/industry', '/trial/volume', '/trial/references', '/trial/problems', '/trial/source', '/trial/complete','/login', '/signup','/dashboard/tracking-request', '/dashboard/shipments', '/dashboard/emails', '/dashboard/tracking-results', '/dashboard/billing', '/dashboard/settings', '/dashboard/settings/data-import', '/dashboard/settings/fields', '/dashboard/not-found', '/dashboard/purchase-orders', '/dashboard/alerts', '/dashboard/support', '/dashboard/support/new', '/dashboard/support/tickets', '/dashboard/support/tickets/new', '/dashboard/support/help', '/dashboard/support/help/questions', '/dashboard/support/help/tours'],
    ai: {
      assistant: {
        enabled: true,
        availableActions: ['analyze-shipment', 'extract-document-data', 'compare-documents', 'validate-invoice', 'reconcile-purchase-orders']
      }
    },
    shipments: {
      maxItems: FREEMIUM_LIMIT
    }
  },
  'Premium': {
    modules: ['tracking', 'shipments', 'emails', 'analytics', 'reports', 'settings', 'purchase-orders', 'import_data', 'fields-management', 'documents', 'alerts', 'usage-analytics', 'support', 'warehouse', 'docgen', 'dashboards', 'assistant', 'control-tower', 'order-audit', 'container-control'],
    features: ['advanced_tracking', 'advanced_shipments', 'analytics', 'custom_reports', 'advanced_settings'],
    routes: ['/trial/start', '/trial/company', '/trial/industry', '/trial/volume', '/trial/references', '/trial/problems', '/trial/source', '/trial/complete','/login', '/signup','/dashboard/tracking-request', '/dashboard/shipments', '/dashboard/emails', '/dashboard/tracking-results', '/dashboard/billing', '/dashboard/settings', '/dashboard/settings/data-import', '/dashboard/settings/fields', '/dashboard/not-found', '/public/shipments/:id', '/dashboard/purchase-orders', '/dashboard/alerts', '/dashboard/support', '/dashboard/support/new', '/dashboard/support/tickets', '/dashboard/support/tickets/new', '/dashboard/support/help', '/dashboard/support/help/questions', '/dashboard/support/help/tours'],

    ai: {
      assistant: {
        enabled: true,
        availableActions: ['summarize', 'analyze', 'suggest']
      },
      actions: {
        enabled: true,
        availableActions: ['translate', 'summarize', 'analyze', 'extract']
      }
    }
  },
  'Enterprise': {
    modules: ['tracking', 'shipments', 'emails', 'analytics', 'reports', 'settings', 'admin', 'purchase-orders', 'import_data', 'fields-management', 'documents', 'alerts', 'usage-analytics', 'support', 'warehouse', 'docgen', 'dashboards', 'assistant', 'control-tower', 'order-audit', 'container-control'],
    features: ['advanced_tracking', 'advanced_shipments', 'analytics', 'custom_reports', 'admin_tools', 'advanced_settings'],
    routes: ['/trial/start', '/trial/company', '/trial/industry', '/trial/volume', '/trial/references', '/trial/problems', '/trial/source', '/trial/complete','/login', '/signup','/dashboard/tracking-request', '/dashboard/shipments', '/dashboard/emails', '/dashboard/tracking-results', '/dashboard/billing', '/dashboard/settings', '/dashboard/settings/data-import', '/dashboard/settings/fields', '/dashboard/not-found', '/public/shipments/:id', '/dashboard/purchase-orders', '/dashboard/alerts', '/dashboard/support', '/dashboard/support/new', '/dashboard/support/tickets', '/dashboard/support/tickets/new', '/dashboard/support/help', '/dashboard/support/help/questions', '/dashboard/support/help/tours'],

    ai: {
      assistant: {
        enabled: true,
        availableActions: ['summarize', 'analyze', 'suggest', 'predict']
      },
      actions: {
        enabled: true,
        availableActions: ['translate', 'summarize', 'analyze', 'extract', 'predict']
      }
    }
  }
}

// Función helper para verificar si un módulo está disponible para un plan
export const isModuleAvailableForPlan = (planName: string, moduleName: string): boolean => {
  const plan = planModules[planName]
  if (!plan) return false
  return plan.modules.includes(moduleName)
}

// Función helper para verificar si una característica está disponible para un plan
export const isFeatureAvailableForPlan = (planName: string, featureName: string): boolean => {
  const plan = planModules[planName]
  if (!plan || !plan.features) return false
  return plan.features.includes(featureName)
}

// Función helper para obtener la configuración del Asistente IA
export const getAIAssistantConfig = (planName: string): AIFeatureConfig => {
  const plan = planModules[planName]
  //console.log('Plan:', plan)
  return plan?.ai?.assistant || { enabled: false }
}

// Función helper para obtener la configuración de las Acciones IA
export const getAIActionsConfig = (planName: string): AIFeatureConfig => {
  const plan = planModules[planName]
  return plan?.ai?.actions || { enabled: false }
}

// Función helper para verificar si una acción específica de IA está disponible
export const isAIActionAvailable = (
  planName: string, 
  actionName: string, 
  type: 'assistant' | 'actions'
): boolean => {
  const plan = planModules[planName]
  const config = type === 'assistant' ? plan?.ai?.assistant : plan?.ai?.actions
  return config?.availableActions?.includes(actionName) || false
}

// Obtener todos los módulos disponibles para un plan
export const getAvailableModulesForPlan = (planName: string): string[] => {
  const plan = planModules[planName]
  return plan?.modules || []
}

// Obtener todas las características disponibles para un plan
export const getAvailableFeaturesForPlan = (planName: string): string[] => {
  const plan = planModules[planName]
  return plan?.features || []
}
